import React from 'react'
import Styles from './AnalyticsDataCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { IFText, IFSkeleton, IFsvg } from 'Components'

const AnalyticsDataCard = ({
  size = 'large',
  title,
  subtitle,
  body,
  primaryValue,
  secondaryValue,
  primaryLabel,
  secondaryLabel,
  percentage,
  unit,
  isLoading,
  flex = 1,
  showDash = false,
}) => {
  const renderValueOrDash = (value) => (showDash ? '-' : value)
  const renderUnit = () => (!showDash && unit ? unit : null)

  return (
    <div
      style={{
        backgroundColor: Colors.Analytics.AnalyticsDataCard,
        flex,
      }}
      className={Styles.Card}
    >
      {isLoading ? (
        <IFSkeleton variant="text" width={120} height={20} />
      ) : (
        <IFText
          style={{
            color:
              size === 'small'
                ? Colors.Analytics.AnalyticsDataGreyText
                : Colors.black,
          }}
          className={Styles.Title}
        >
          {title}
        </IFText>
      )}

      {size === 'small' ? (
        <div className={Styles.SmallDiv}>
          {isLoading ? (
            <IFSkeleton variant="text" width={60} height={28} />
          ) : (
            <>
              <IFText className={Styles.PrimaryValue}>
                {renderValueOrDash(primaryValue)}
              </IFText>
              {renderUnit() && (
                <IFText className={Styles.Unit}>{renderUnit()}</IFText>
              )}
            </>
          )}
        </div>
      ) : (
        <div className={Styles.Main}>
          {secondaryValue !== undefined ? (
            <div className={Styles.SecondaryDiv}>
              <div className={Styles.FullWidth}>
                {isLoading ? (
                  <div className={Styles.SecondaryDiv}>
                    {[1, 2].map((i) => (
                      <div key={i}>
                        <IFSkeleton
                          variant="text"
                          className={Styles.PrimaryValue}
                          width={60}
                          height={16}
                        />
                        <IFSkeleton
                          className={Styles.Label}
                          width={60}
                          height={16}
                          variant="text"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={Styles.SecondaryDiv}>
                    <div className={Styles.LeftDiv}>
                      <IFText className={Styles.PrimaryValue}>
                        {renderValueOrDash(primaryValue)} {renderUnit()}
                      </IFText>
                      <IFText className={Styles.Label}>{primaryLabel}</IFText>
                    </div>

                    <div
                      className={Styles.Divider}
                      style={{
                        border: `0.5px dashed ${Colors.pinFieldBorder}`,
                      }}
                    />

                    <div className={Styles.RightDiv}>
                      <IFText className={Styles.PrimaryValue}>
                        {renderValueOrDash(secondaryValue)} {renderUnit()}
                      </IFText>
                      <IFText className={Styles.Label}>{secondaryLabel}</IFText>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              {percentage !== undefined ? (
                <div className={Styles.PercentageContainer}>
                  {isLoading ? (
                    <IFSkeleton variant="text" width={120} height={20} />
                  ) : showDash ? (
                    <IFText className={Styles.Percentage}>-</IFText>
                  ) : (
                    <>
                      <IFsvg.ArrowOutward
                        fill={
                          percentage > 0
                            ? Colors.Analytics.GreenPercentage
                            : Colors.red
                        }
                        transform={percentage > 0 ? 'none' : 'rotate(90deg)'}
                        className={Styles.Arrow}
                        height={16}
                        width={16}
                      />
                      <IFText
                        style={{
                          color:
                            percentage > 0
                              ? Colors.Analytics.GreenPercentage
                              : Colors.red,
                        }}
                        className={Styles.Percentage}
                      >
                        {`${Math.abs(
                          parseFloat(percentage.toFixed(1)),
                        ).toLocaleString()}%`}
                      </IFText>
                      <IFText className={Styles.Subtitle}>{subtitle}</IFText>
                    </>
                  )}
                </div>
              ) : isLoading ? (
                <IFSkeleton variant="text" width={155} height={20} />
              ) : (
                <IFText className={Styles.SubtitleNoPercent}>{subtitle}</IFText>
              )}

              <div className={Styles.ValueDiv}>
                {isLoading ? (
                  <IFSkeleton variant="text" width={80} height={28} />
                ) : (
                  <>
                    <IFText className={Styles.PrimaryValue}>
                      {renderValueOrDash(primaryValue)}
                    </IFText>
                    {renderUnit() && (
                      <IFText className={Styles.Unit}>{renderUnit()}</IFText>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {isLoading ? (
            <IFSkeleton variant="text" width={155} height={18} />
          ) : (
            <IFText>{body}</IFText>
          )}
        </div>
      )}
    </div>
  )
}

AnalyticsDataCard.propTypes = {
  size: PropTypes.oneOf(['small', 'large']),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  body: PropTypes.string,
  primaryValue: PropTypes.number,
  secondaryValue: PropTypes.number,
  primaryLabel: PropTypes.string,
  secondaryLabel: PropTypes.string,
  percentage: PropTypes.number,
  unit: PropTypes.string,
  isLoading: PropTypes.bool,
  flex: PropTypes.number,
  showDash: PropTypes.bool,
}

export default AnalyticsDataCard
